import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link, useSearchParams } from 'react-router-dom'; // Import router hooks, Link, and useSearchParams
import { useAppContext } from '../context/AppContext'; // Import useAppContext
import { useAuth } from '../context/AuthContext'; // Import useAuth for permission checks
import { reportNavigationVitals } from '../reportWebVitals'; // Import navigation vitals
import { ChevronRight, ArrowLeft } from 'lucide-react'; // Removed ClipboardCopy and Check
// import { programData } from '../../data/programData'; // Remove fallback data
import { getMainTabs, getSubTabs } from '../data/navData'; // Corrected path and import functions
import apiService from '../services/api'; // Corrected path
import { getLanguageColors } from '../config'; // Import language color helper

// Overview subtabs
import ProgramSummary from '../components/program/ProgramSummary';
import ProgramTitles from '../components/program/ProgramTitles';
import ProgramDescriptions from '../components/program/ProgramDescriptions';
import ProgramReleases from '../components/program/ProgramReleases';
import ProgramRatings from '../components/program/ProgramRatings';
import ProgramAwards from '../components/program/ProgramAwards';

// Main tab components
import SeasonsEpisodes from '../components/seasons/SeasonsEpisodes';
import SeasonImages from '../components/seasons/SeasonImages';
import CastCrew from '../components/castcrew/CastCrew';
import Images from '../components/images/Images';
// import OtherVersions from '../components/versions/OtherVersions'; // Temporarily hidden
import VideoDescriptors from '../components/descriptors/VideoDescriptors';
import ProgramAirings from '../components/program/ProgramAirings';
import ProgramAvailabilities from '../components/program/ProgramAvailabilities';
import ProgramVersions from '../components/program/ProgramVersions';
import ProgramSports from '../components/program/ProgramSports';
import ProgramChildren from '../components/program/ProgramChildren';
import ProgramEncoreAI from '../components/program/ProgramEncoreAI'; // Import new component
import CopyableId from '../components/common/CopyableId'; // Import shared component
import SaveButton from '../components/common/SaveButton'; // Import save button component

const ProgramDetailsPage = () => { // Renamed component
  const { programId } = useParams(); // Get programId from URL
  const navigate = useNavigate(); // Hook for navigation
  const { t, language } = useAppContext(); // Get translation function and language
  const { hasPermission } = useAuth(); // Get permission check function
  const [searchParams, setSearchParams] = useSearchParams(); // Hook for URL query parameters

  // Local state for tabs - initialize from URL params if present
  const [activeMainTab, setActiveMainTab] = useState(searchParams.get('tab') || 'overview');
  const [activeSubTab, setActiveSubTab] = useState('summary');
  const [selectedSeason, setSelectedSeason] = useState(searchParams.get('season') || null);
  const [highlightedEpisode, setHighlightedEpisode] = useState(searchParams.get('episode') || null);

  // Sync state with URL parameters when URL changes (e.g., back button)
  useEffect(() => {
    const tabFromUrl = searchParams.get('tab');
    const seasonFromUrl = searchParams.get('season');
    const episodeFromUrl = searchParams.get('episode');
    
    if (tabFromUrl && tabFromUrl !== activeMainTab) {
      setActiveMainTab(tabFromUrl);
    } else if (!tabFromUrl && activeMainTab !== 'overview') {
      setActiveMainTab('overview');
    }
    
    if (seasonFromUrl !== null) {
      // Parse season - it might be a number or a string
      const parsedSeason = isNaN(seasonFromUrl) ? seasonFromUrl : parseInt(seasonFromUrl, 10);
      if (parsedSeason !== selectedSeason) {
        setSelectedSeason(parsedSeason);
      }
    } else if (seasonFromUrl === null && selectedSeason !== null) {
      setSelectedSeason(null);
    }

    if (episodeFromUrl !== highlightedEpisode) {
      setHighlightedEpisode(episodeFromUrl);
    }
  }, [searchParams]);

  // Track tab changes for Web Vitals
  useEffect(() => {
    // Skip on initial mount (only track actual tab changes)
    if (activeMainTab) {
      const timer = setTimeout(() => {
        console.log(`📑 Tab changed to: ${activeMainTab}${activeMainTab === 'overview' ? ` > ${activeSubTab}` : ''}`);
        reportNavigationVitals();
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [activeMainTab, activeSubTab]);

  const [showSeasonsEpisodesTab, setShowSeasonsEpisodesTab] = useState(false); // Renamed state
  const [showSeasonImagesTab, setShowSeasonImagesTab] = useState(false);
  const [programOverview, setProgramOverview] = useState(null); // Holds fetched program data
  const [episodeDetails, setEpisodeDetails] = useState(null);
  const [isLoading, setIsLoading] = useState(true); // Start loading true
  const [numAirings, setNumAirings] = useState(null);
  const [numAvailabilities, setNumAvailabilities] = useState(null);
  const [error, setError] = useState(null); // State for errors

  // Helper function to get the program type text (remains the same)
  const getProgramTypeText = (overview) => {
    if (!overview) return 'SERIES'; // Default fallback

    // Extract progType and subType
    const progType = overview.progType;
    const subType = overview.subType;

    // Apply the business rules
    if (progType === 'Series') {
      if (subType === 'Series') return 'SERIES';
      if (subType === 'Episode') return 'SERIES EPISODE';
      return 'SERIES'; // Default for Series
    }

    if (progType === 'Podcast') {
      if (subType === 'Podcast') return 'PODCAST';
      if (subType === 'Episode') return 'PODCAST EPISODE';
      return 'PODCAST';
    }

    if (progType === 'Feature Film') return 'FEATURE FILM';

    // Default case - use the progType
    return progType ? progType.toUpperCase() : 'SERIES';
  };

  // Helper function to get theme color based on program type
  const getThemeColor = () => {
    if (!programOverview) {
      return {
        main: '#4f46e5', // indigo-600
        dark: '#4338ca', // indigo-700
        light: '#eef2ff' // indigo-50
      };
    }

    const progType = programOverview.progType;
    const subType = programOverview.subType;

    if (progType === 'Series') {
      if (subType === 'Series') {
        return {
          main: '#4f46e5', // indigo-600
          dark: '#4338ca', // indigo-700
          light: '#eef2ff' // indigo-50
        };
      }
      if (subType === 'Episode') {
        return {
          main: '#2563eb', // blue-600
          dark: '#1d4ed8', // blue-700
          light: '#eff6ff' // blue-50
        };
      }
    }

    if (progType === 'Podcast') {
      if (subType === 'Podcast') {
        return {
          main: '#7c3aed', // violet-600
          dark: '#6d28d9', // violet-700
          light: '#f5f3ff' // violet-50
        };
      }
      if (subType === 'Episode') {
        return {
          main: '#8b5cf6', // violet-500
          dark: '#7c3aed', // violet-600
          light: '#f5f3ff' // violet-50
        };
      }
    }

    if (progType === 'Feature Film') {
      return {
        main: '#d97706', // amber-600
        dark: '#b45309', // amber-700
        light: '#fffbeb' // amber-50
      };
    }

    return {
      main: '#059669', // emerald-600
      dark: '#047857', // emerald-700
      light: '#ecfdf5' // emerald-50
    };
  };

  // Fetch program data based on programId from URL
  useEffect(() => {
    const fetchProgramData = async () => {
      if (!programId) {
        setError("No Program ID provided.");
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      setError(null);
      setProgramOverview(null);
      setEpisodeDetails(null);
      setNumAirings(null);
      setNumAvailabilities(null);
      
      // Only reset tabs if no URL params are present (fresh navigation)
      if (!searchParams.get('tab')) {
        setActiveMainTab('overview');
        setActiveSubTab('summary');
        setSelectedSeason(null);
      }

      try {
        // Fetch program overview
        const overviewData = await apiService.getOverview(programId);
        if (!overviewData) {
          throw new Error("Program not found.");
        }

        // If image_url is empty, fetch all images and apply fallback logic
        if (!overviewData.image_url) {
          try {
            const allImages = await apiService.getImages(programId, 480);
            if (allImages && allImages.length > 0) {
              // Fallback logic:
              // 1. Try to find a power art (Iconic) with 16:9 aspect ratio
              let selectedImage = allImages.find(img => 
                img.category === 'Iconic' && img.ratio === '16:9'
              );

              // 2. If not found, try VOD Art with 16:9 at the highest width available
              if (!selectedImage) {
                const vodArt16x9 = allImages.filter(img => 
                  img.category === 'VOD Art' && img.ratio === '16:9'
                );
                if (vodArt16x9.length > 0) {
                  // Sort by width descending and pick the highest width
                  vodArt16x9.sort((a, b) => (b.width || 0) - (a.width || 0));
                  selectedImage = vodArt16x9[0];
                }
              }

              // 3. If not found, try any image with 16:9 aspect ratio
              if (!selectedImage) {
                selectedImage = allImages.find(img => img.ratio === '16:9');
              }

              // 4. If still not found, pick any remaining image
              if (!selectedImage) {
                selectedImage = allImages[0];
              }

              // Set the image_url from the selected image
              if (selectedImage && selectedImage.image_url) {
                overviewData.image_url = selectedImage.image_url;
              }
            }
          } catch (imgErr) {
            console.warn('Failed to fetch fallback images (non-critical):', imgErr);
          }
        }

        setProgramOverview(overviewData);

        // Determine if Seasons & Episodes tab should be shown
        const shouldShowSeasonsEpisodes =
          (overviewData.progType === 'Series' && overviewData.subType === 'Series') ||
          (overviewData.progType === 'Podcast' && overviewData.subType === 'Podcast') ||
          (overviewData.progType === 'Miniseries' && overviewData.subType === 'Miniseries') ||
          (overviewData.progType === 'Sports non-event' && overviewData.subType === 'Series');
          // Removed condition for sports event: || (overviewData.progType === 'Sports event' && overviewData.subType === 'Sport');
        setShowSeasonsEpisodesTab(shouldShowSeasonsEpisodes);
        setShowSeasonImagesTab(shouldShowSeasonsEpisodes);

        // If it's a Sports Event or Series Episode, fetch episode details (Keep fetching for sports event if needed elsewhere)
        if (overviewData.progType === 'Sports event' || ((overviewData.progType === 'Series' || overviewData.progType === 'Podcast') && overviewData.subType === 'Episode')) {
          try {
            const episodeData = await apiService.getEpisodeDetails(programId);
            setEpisodeDetails(episodeData); // Can be null if not found
          } catch (epErr) {
            console.warn('Failed to fetch episode details (non-critical):', epErr);
            setEpisodeDetails(null);
          }
        }

        // Fetch number of scheduled airings
        const airingsCount = await apiService.getNumScheduled(programId);
        setNumAirings(airingsCount);

        // Fetch number of availabilities
        const availabilitiesCount = await apiService.getNumAvailabilities(programId);
        setNumAvailabilities(availabilitiesCount);

      } catch (err) {
        console.error('Failed to fetch program data:', err);
        setError(err.message || 'Failed to load program details.');
      } finally {
        setIsLoading(false);
      }
    };

    fetchProgramData();
  }, [programId]); // Re-run effect when programId changes (not searchParams to avoid unnecessary reloads)

  // Effect to reset main tab if it becomes invalid (e.g., 'seasonsEpisodes' for non-applicable types)
  useEffect(() => {
    if (!isLoading && programOverview) {
      if (activeMainTab === 'seasonsEpisodes' && !showSeasonsEpisodesTab) {
        setActiveMainTab('overview');
        setSearchParams({}); // Clear URL params
      }
      // Temporarily hide 'Other Versions' tab - reset if active
      if (activeMainTab === 'otherVersions') {
         setActiveMainTab('overview');
         setSearchParams({}); // Clear URL params
      }
      // Reset to overview if sports tab is active but it's not a sports event
      if (activeMainTab === 'sports' && programOverview.progType.toLowerCase() !== 'sports event') {
        setActiveMainTab('overview');
        setSearchParams({}); // Clear URL params
      }
    }
  }, [showSeasonsEpisodesTab, activeMainTab, programOverview, isLoading, setSearchParams]);

  // Handler to update active main tab and URL
  const handleMainTabChange = (tabId) => {
    setActiveMainTab(tabId);
    if (tabId === 'seasonsEpisodes') {
      const params = { tab: tabId };
      if (selectedSeason) params.season = selectedSeason;
      if (highlightedEpisode) params.episode = highlightedEpisode;
      setSearchParams(params);
    } else if (tabId === 'overview') {
      setSearchParams({}); // Clear params for overview tab
    } else {
      setSearchParams({ tab: tabId }); // Set tab param for other tabs
    }
  };

  // Handler for season change from SeasonsEpisodes component
  const handleSeasonChange = (season) => {
    setSelectedSeason(season);
    if (activeMainTab === 'seasonsEpisodes') {
      // Ensure season is converted to string for URL
      const params = { tab: 'seasonsEpisodes', season: String(season) };
      if (highlightedEpisode) params.episode = highlightedEpisode;
      setSearchParams(params);
    }
  };

  // Get translated tabs
  const currentMainTabs = getMainTabs(t);
  const currentSubTabs = getSubTabs(t);

  // Filter main tabs based on program type and data availability
  const filteredMainTabs = currentMainTabs.filter(tab => {
    if (!programOverview) return false; // Don't show tabs until overview is loaded

    if (tab.id === 'seasonsEpisodes' && !showSeasonsEpisodesTab) return false;
    if (tab.id === 'seasonImages' && !showSeasonImagesTab) return false;
    if (tab.id === 'otherVersions') return false; // Temporarily hide
    if (tab.id === 'airings' && numAirings === 0) return false;
    if (tab.id === 'availabilities' && numAvailabilities === 0) return false;
    // Hide sports tab if NOT a sports event, OR if it's a sports event with subtype 'Sport'
    if (tab.id === 'sports') {
      if (programOverview.progType.toLowerCase() !== 'sports event') return false;
      if (programOverview.progType === 'Sports event' && programOverview.subType === 'Sport') return false;
    }
    // Condition for showing the 'Children' tab
    if (tab.id === 'children') {
      const isSportsEvent = programOverview.progType === 'Sports event';
      // Corrected condition: Show only if connectorId is the SAME as programId
      const hasSameConnectorId = programOverview.connectorId && programOverview.connectorId === programId;
      if (!isSportsEvent || !hasSameConnectorId) { // Hide if not sports event OR connectorId is different/missing
        return false;
      }
    }
    // Hide Encore AI tab if user doesn't have encore.ai permission
    if (tab.id === 'encoreAI' && !hasPermission('encore.ai')) {
      return false;
    }

    return true;
  });

  // *** MOVED renderSubTabContent BEFORE renderMainTabContent ***
  // Render sub tab content based on active sub tab (only for Overview main tab)
  const renderSubTabContent = useCallback(() => {
    if (!programOverview) return null;

    // Pass programOverview or specific parts to sub-components if needed
    // For now, assuming they might fetch their own data using programId or context
    const props = { programId, programOverview };

    switch (activeSubTab) {
      case 'summary':
        // ProgramSummary might need programOverview directly
        return <ProgramSummary programId={programId} programData={programOverview} />;
      case 'titles':
        return <ProgramTitles {...props} />;
      case 'descriptions':
        return <ProgramDescriptions {...props} />;
      case 'releases':
        return <ProgramReleases {...props} />;
      case 'ratings':
        return <ProgramRatings {...props} />;
      case 'awards':
        return <ProgramAwards {...props} />;
      default:
        return <div className="p-6 text-center text-gray-500">Select a sub-tab.</div>;
    }
  }, [activeSubTab, programId, programOverview]); // Add dependencies

  // Render main tab content based on active main tab (pass programId if needed)
  const renderMainTabContent = useCallback(() => {
    if (!programOverview) return null; // Or a loading indicator

    // Pass programId to components that might need it directly
    const props = { programId };

    switch (activeMainTab) {
      case 'overview':
        return renderSubTabContent(); // Sub-tab rendering handles its own props
      case 'seasonsEpisodes':
        return <SeasonsEpisodes {...props} selectedSeason={selectedSeason} onSeasonChange={handleSeasonChange} highlightedEpisode={highlightedEpisode} />;
      case 'seasonImages':
        return <SeasonImages {...props} />;
      case 'castCrew':
        return <CastCrew {...props} />;
      case 'images':
        return <Images {...props} />;
      case 'videoDescriptors':
        return <VideoDescriptors {...props} />;
      case 'airings':
        return <ProgramAirings {...props} />;
      case 'availabilities':
        return <ProgramAvailabilities {...props} />;
      case 'versions':
        return <ProgramVersions {...props} />;
      case 'sports':
        return <ProgramSports {...props} />;
      case 'children':
        // Pass the connectorId from the programOverview
        return <ProgramChildren connectorId={programOverview?.connectorId} />;
      case 'encoreAI':
        return <ProgramEncoreAI tmsId={programId} />;
      default:
        return <div className="p-6 text-center text-gray-500">Select a tab.</div>;
    }
  }, [activeMainTab, programId, programOverview, selectedSeason, highlightedEpisode, renderSubTabContent]); // Add selectedSeason and highlightedEpisode to dependencies

  // Determine which image URL to use (primarily from programOverview)
  const imageUrl = programOverview?.image_url || null;

  // Check if we should show hierarchical breadcrumbs
  const showHierarchicalBreadcrumbs = programOverview?.connectorId &&
                                     programOverview?.connectorId !== programId;

  // Helper to get the program duration in minutes
  const getDuration = () => {
    return programOverview?.duration ? `${programOverview.duration} mins` : '';
  };

  // Helper to get the program year
  const getYear = () => {
    if (programOverview?.yearOfRelease) return programOverview.yearOfRelease;
    if (programOverview?.premiere_date) return programOverview.premiere_date.substring(0, 4);
    return '';
  };

  // Get current theme color for styling
  const themeColor = getThemeColor(); // This function uses programOverview state

  // Handle loading and error states
  if (isLoading) {
    return <div className="p-6 text-center">{t('programDetails.loadingProgramDetails')}</div>;
  }

  if (error) {
    return <div className="p-6 text-center text-red-600">Error: {error}</div>;
  }

  if (!programOverview) {
    // Should not happen if loading/error handled, but as a fallback
    return <div className="p-6 text-center">Program data not available.</div>;
  }

  // Main render logic starts here, using programOverview
  return (
    <div className="flex flex-col w-full h-full">
      {/* Header with breadcrumbs - Updated */}
      <div className="p-2 pl-4 border-b border-gray-300 flex items-center justify-between text-sm bg-gray-50">
        <div className="flex items-center">
          <Link to="/" className="text-gray-500 hover:text-blue-600">{t('programDetails.search')}</Link>
          <ChevronRight className="h-3 w-3 mx-1 text-gray-400" />
          <span className="text-gray-500">{t('programDetails.title')}</span>
          <ChevronRight className="h-3 w-3 mx-1 text-gray-400" />

          {/* Show hierarchical breadcrumbs if program has a different parent connectorId */}
          {showHierarchicalBreadcrumbs ? (
            <>
              <span
                className="text-gray-600 cursor-pointer hover:text-blue-600 transition-colors font-medium"
                onClick={() => navigate(`/program/${programOverview.connectorId}`)} // Use navigate
              >
                {programOverview.connectorId}
              </span>
              <ChevronRight className="h-3 w-3 mx-1 text-gray-400" />
              <span className="text-gray-600 font-medium">{programId}</span>
            </>
          ) : (
            <span className="text-gray-600 font-medium">{programId}</span>
          )}
        </div>
        {/* Save Button - Moved to header */}
        <div className="px-4 py-2 border border-gray-300 bg-white text-gray-700 rounded-sm text-sm font-medium flex items-center hover:bg-gray-50 mr-4">
          <SaveButton
            tmsId={programId}
            connectorId={programOverview?.connectorId}
            programType={getProgramTypeText(programOverview)}
            showText={true}
          />
        </div>
      </div>

      {/* Main content */}
      <div className="flex flex-col bg-gray-100 h-full overflow-y-auto">
        {/* Series info and poster */}
        <div className="flex bg-white border-b border-gray-200">
          <div className="p-6 pl-8 flex-1 mt-0" style={{ borderLeft: `4px solid ${themeColor.dark}` }}>
            {/* Program type tag */}
            <div className="mb-2">
              <span className="text-sm font-semibold uppercase" style={{ color: themeColor.dark }}>
                {getProgramTypeText(programOverview)}
              </span>
            </div>

            {/* Title */}
            <h1 className="text-4xl font-bold text-left">{programOverview.title || 'No Title'}</h1>

            {/* Episode Title (for Sports Events / Series Episodes) */}
            {(episodeDetails?.episode_title) && (
              <h2 className="text-xl font-medium text-gray-600 mb-4 text-left">
                {episodeDetails.episode_title}
              </h2>
            )}

            {/* Popularity score */}
            <div className="flex mb-4 justify-start items-center">
              <div className="flex items-center">
                <svg className="h-4 w-4 mr-1" fill="currentColor" viewBox="0 0 20 20" style={{ color: themeColor.main }}>
                  <path fillRule="evenodd" d="M10 15.585L16.2 19l-1.5-6.5L20 7.5l-6.5-.5L10 1 7 7l-6.5.5L4.8 12.5 3 19l7-3.5z"/>
                </svg>
                <span className="text-sm font-medium" style={{ color: themeColor.main }}>
                  {t('programDetails.popularityScore')} {programOverview?.popularityScore || 'N/A'}
                </span>
              </div>
            </div>

            {/* Details row */}
            <div className="flex text-gray-600 text-sm mb-4 justify-start flex-wrap">
              {/* Genre */}
              {programOverview?.genres && programOverview.genres.length > 0 && (
                <span className="mr-2 mb-1">{programOverview.genres.map(g => g.genre).join(', ')}</span>
              )}

              {/* Network - Assuming network is part of overview, adjust if needed */}
              {programOverview?.network && (
                <>
                  <span className="mx-2">•</span>
                  <span className="mr-2 mb-1">{programOverview.network}</span>
                </>
              )}

              {/* Language */}
              {(programOverview?.title_lang || programOverview?.desc_lang || episodeDetails?.episode_title_lang) && (
                <>
                  <span className="mx-2">•</span>
                  <span className="flex items-center flex-wrap">
                    {programOverview.title_lang && (() => {
                      const colors = getLanguageColors(programOverview.title_lang);
                      return (
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${colors.bg} ${colors.text} mr-2 mb-1`}>
                          {t('programDetails.titleLanguage')}: {programOverview.title_lang === 'en' ? t('settings.language.english') : programOverview.title_lang.toUpperCase()}
                        </span>
                      );
                    })()}
                    {programOverview.desc_lang && (() => {
                      const colors = getLanguageColors(programOverview.desc_lang);
                      return (
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${colors.bg} ${colors.text} mr-2 mb-1`}>
                          {t('programDetails.descriptionLanguage')}: {programOverview.desc_lang === 'en' ? t('settings.language.english') : programOverview.desc_lang.toUpperCase()}
                        </span>
                      );
                    })()}
                    {episodeDetails?.episode_title_lang && (() => {
                      const colors = getLanguageColors(episodeDetails.episode_title_lang);
                      return (
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${colors.bg} ${colors.text} mr-2 mb-1`}>
                          {t('programDetails.episodeTitleLanguage')}: {episodeDetails.episode_title_lang === 'en' ? t('settings.language.english') : episodeDetails.episode_title_lang.toUpperCase()}
                        </span>
                      );
                    })()}
                  </span>
                </>
              )}

              {/* Duration */}
              {getDuration() && (
                <>
                  <span className="mx-2">•</span>
                  <span className="mr-2 mb-1">{getDuration()}</span>
                </>
              )}

              {/* Year */}
              {getYear() && (
                <>
                  <span className="mx-2">•</span>
                  <span className="mr-2 mb-1">{getYear()}</span>
                </>
              )}
            </div>

            {/* Description */}
            <p className="text-gray-700 mb-6 text-sm text-left">
              {programOverview?.description || t('programDetails.noDescription')}
            </p>

            {/* Episode Information (for Series Episodes) */}
            {(programOverview?.episode_season || programOverview?.episode_number || programOverview?.episode_seasonYear) && (
              <div className="text-sm text-gray-600 mb-4 text-left flex flex-wrap gap-x-4">
                {programOverview.episode_seasonYear && (
                  <span className="font-medium">
                    {t('programDetails.seasonYear')} {programOverview.episode_seasonYear}
                  </span>
                )}
                {programOverview.episode_season && (
                  <span className="font-medium">
                    {t('programDetails.season')} {programOverview.episode_season}
                  </span>
                )}
                {programOverview.episode_number && (
                  <span className="font-medium">
                    {t('programDetails.seasonEpisode')} {programOverview.episode_number}
                  </span>
                )}
              </div>
            )}

            {/* IDs Section */}
            <div className="text-xs text-gray-500 mb-4 text-left">
              <CopyableId label="TMS ID" value={programId} /> {/* Use programId from URL */}

              {programOverview?.rootId && (
                <CopyableId label="Root ID" value={programOverview.rootId} />
              )}

              {programOverview?.seriesId && (
                <CopyableId label="Series ID" value={programOverview.seriesId} />
              )}
            </div>

            {/* Action buttons - Simplified */}
            <div className="flex justify-start mb-4 mt-6 space-x-4">
               {/* General Back Button */}
               <button
                className="px-4 py-2 border border-gray-300 bg-white text-gray-700 rounded-sm text-sm font-medium flex items-center hover:bg-gray-50"
                onClick={() => navigate(-1)} // Use navigate for back
              >
                <ArrowLeft className="h-4 w-4 mr-1" />
                <span>{t('programDetails.backButton')}</span>
              </button>

              {/* View Airings Button */}
              <button
                className={`px-4 py-2 border border-gray-300 bg-white text-gray-700 rounded-sm text-sm font-medium flex items-center ${numAirings === 0 ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-50'}`}
                disabled={numAirings === 0}
                onClick={() => handleMainTabChange('airings')}
              >
                <svg className="h-4 w-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16m-7 6h7"/>
                </svg>
                <span>
                  {numAirings === null ? t('programDetails.viewProgramAirings') :
                   numAirings === 0 ? t('programDetails.noAirings') :
                   t('programDetails.viewNAirings', { count: numAirings })}
                </span>
              </button>
              {/* View Availabilities Button */}
              <button
                className={`px-4 py-2 border border-gray-300 bg-white text-gray-700 rounded-sm text-sm font-medium flex items-center ${numAvailabilities === 0 ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-50'}`}
                disabled={numAvailabilities === 0}
                onClick={() => handleMainTabChange('availabilities')}
              >
                <svg className="h-4 w-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z"/>
                </svg>
                <span>
                  {numAvailabilities === null ? t('programDetails.viewAvailabilities') :
                   numAvailabilities === 0 ? t('programDetails.noAvailabilities') :
                   t('programDetails.showNAvailabilities', { count: numAvailabilities })}
                </span>
              </button>
            </div>

            {/* Last updated timestamp */}
            <div className="text-xs text-gray-400 mt-6 text-right">
              {t('programDetails.lastUpdated')} {programOverview?.updateDate ? new Date(programOverview.updateDate).toLocaleDateString(language, { // Use dynamic language
                year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
              }) : t('programDetails.lastUpdatedUnknown')}
            </div>
          </div>

          {/* Image container */}
          <div className="w-96 flex items-center justify-center p-4 bg-gray-50 mr-6 rounded-md">
            {imageUrl ? (
              <img
                src={imageUrl}
                alt={programOverview.title || 'Program Image'}
                className="max-h-[480px] max-w-full object-contain"
                onError={(e) => { e.target.src = 'https://panorama.telus.encoreinteractive.ca/offline.png'; }}
              />
            ) : (
              <div className="text-gray-800 text-center p-4">
                 {/* Display title parts if no image */}
                 {programOverview.title && (
                   <>
                    <div className="text-4xl font-bold mt-6 mb-1 break-words">{programOverview.title.split(' ')[0]}</div>
                    {programOverview.title.split(' ').length > 1 && (
                      <div className="text-4xl font-bold mb-6 break-words">
                        {programOverview.title.split(' ').slice(1).join(' ')}
                      </div>
                    )}
                   </>
                 )}
              </div>
            )}
          </div>
        </div>

        {/* Main tabs */}
        <div className="flex border-b border-gray-300 mt-2 bg-white sticky top-0 z-10">
          {filteredMainTabs.map((tab, index) => (
            <button
              key={tab.id}
              className={`px-4 py-2 text-sm font-medium ${index === 0 ? 'ml-2' : ''}`}
              style={{
                backgroundColor: activeMainTab === tab.id ? themeColor.dark : 'white',
                color: activeMainTab === tab.id ? 'white' : '#4b5563',
                borderBottom: activeMainTab === tab.id ? `2px solid ${themeColor.dark}` : '2px solid transparent'
              }}
              onClick={() => handleMainTabChange(tab.id)}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Sub tabs - only show if Overview is selected */}
        {activeMainTab === 'overview' && (
          <div className="flex border-b border-gray-300 bg-white sticky top-10 z-10">
            {currentSubTabs.map((tab, index) => (
              <button
                key={tab.id}
                className={`px-4 py-2 text-sm ${index === 0 ? 'ml-2' : ''}`}
                style={{
                  borderBottom: activeSubTab === tab.id ? `2px solid ${themeColor.main}` : '2px solid transparent',
                  fontWeight: activeSubTab === tab.id ? '600' : 'normal',
                  color: activeSubTab === tab.id ? themeColor.main : '#4b5563'
                }}
                onClick={() => setActiveSubTab(tab.id)}
              >
                {tab.label}
              </button>
            ))}
          </div>
        )}

        {/* Content based on active main tab and subtab */}
        <div className="bg-white p-0 flex-1">
          {renderMainTabContent()}
        </div>
      </div>
    </div>
  );
};

// Removed local CopyableId component definition

export default ProgramDetailsPage; // Export renamed component
